import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  EditMediaDialogComponent,
  IEditMediaDialogData,
  NgMediaManagerModule,
} from '@principle-theorem/ng-media-manager';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import {
  ConfirmDialogComponent,
  DialogPresets,
  FileManagerService,
  IMediaManagerUpdateEvent,
  IUploadStartedEvent,
  NgCommonModule,
  SelectionListStore,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';
import {
  IReferral,
  IReferralMedia,
  ReferralActor,
  isStafferActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_WITH_YEAR,
  ISODateType,
  WithRef,
  isSameIdentifier,
  isSameRef,
  snapshot,
  toISODate,
} from '@principle-theorem/shared';
import { groupBy } from 'lodash';
import { MomentModule } from 'ngx-moment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable } from 'rxjs';
import { ReferralMediaMenuComponent } from '../referral-media-menu/referral-media-menu.component';
import { ReferralMediaService } from '../../services/referral-media.service';

export interface IGroupedReferralMediaUploader {
  uploader: string;
  media: WithRef<IReferralMedia>[];
}

export interface IGroupedReferralMediaByDate {
  date: ISODateType;
  uploaders: IGroupedReferralMediaUploader[];
}

@Component({
  selector: 'pr-referral-media',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgMediaManagerModule,
    NgxSkeletonLoaderModule,
    MomentModule,
    EmptyStateComponent,
    MatFormFieldModule,
    MatSelectModule,
    ReferralMediaMenuComponent,
  ],
  providers: [SelectionListStore],
  templateUrl: './referral-media.component.html',
  styleUrl: './referral-media.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex h-full flex-col',
  },
})
export class ReferralMediaComponent {
  private _dialog = inject(MatDialog);
  private _fileDownload = inject(FileManagerService);
  private _snackBar = inject(MatSnackBar);
  public referralMediaService = inject(ReferralMediaService);
  public selectionList = inject(SelectionListStore<WithRef<IReferralMedia>>);

  referral = input.required<WithRef<IReferral>>();
  currentUser = input.required<ReferralActor>();
  media = input<WithRef<IReferralMedia>[]>([]);
  canManagePatientMedia = input(false, { transform: coerceBooleanProperty });

  selectMode = signal(false);
  patient = computed(() => this.referral().patient);
  groupedMedia = computed(() => this._groupMedia(this.media()));
  isDisabled = computed(() => !this.patient());
  mediaList = computed(() => this.media() || []);
  selectModeText = computed(() => (this.selectMode() ? 'Clear' : 'Select'));

  readonly dateFormat = CASUAL_DATE_WITH_YEAR;

  @ViewChild('mediaContainer') mediaContainer: ElementRef<HTMLElement>;

  constructor() {
    effect(() => {
      const media = this.media();
      if (media && media.length > 0) {
        setTimeout(() => this._scrollToBottom(), 0);
      }
    });

    this.selectionList.setCompareFn(isSameRef);
    this.selectionList.loadOptions(toObservable(this.media));
  }

  async selectPatientFiles(): Promise<void> {
    const patient = this.patient();
    const referral = this.referral();
    const actor = this.currentUser();

    if (!actor || !isStafferActor(actor)) {
      return;
    }

    await this.referralMediaService.selectPatientFiles(
      patient,
      referral,
      actor,
      this.mediaList()
    );
  }

  canCopyReferralMediaToPatientFiles(
    selected: WithRef<IReferralMedia>[]
  ): boolean {
    if (!this.canManagePatientMedia()) {
      return false;
    }
    return selected.some((media) => !media.patientMediaRef);
  }

  canDeleteSelected(selected: WithRef<IReferralMedia>[]): boolean {
    if (!this.canManagePatientMedia()) {
      return false;
    }

    return selected.every((media) =>
      isSameIdentifier(media.actor, this.currentUser())
    );
  }

  async copyReferralMediaToPatient(): Promise<void> {
    const patient = this.patient();
    const actor = this.currentUser();
    const selected = await snapshot(this.selectionList.selected$);

    if (
      !actor ||
      !selected.length ||
      !this.canCopyReferralMediaToPatientFiles(selected)
    ) {
      return;
    }

    const excludeExistingPatientFiles = selected.filter(
      (media) => !media.patientMediaRef
    );

    if (!excludeExistingPatientFiles.length) {
      this._snackBar.open(
        'The selected media already exists in patient files.'
      );
      return;
    }

    await this.referralMediaService.copyReferralMediaToPatient(
      patient,
      excludeExistingPatientFiles
    );
  }

  async viewMedia(
    media: WithRef<IReferralMedia>[],
    selected: WithRef<IReferralMedia>
  ): Promise<void> {
    const config = DialogPresets.fullscreen<IEditMediaDialogData>({
      data: {
        orderedMedia: media,
        editMode: false,
        readOnly: true,
        media: selected,
      },
    });

    await this._dialog
      .open<
        EditMediaDialogComponent,
        IEditMediaDialogData,
        IMediaManagerUpdateEvent
      >(EditMediaDialogComponent, config)
      .afterClosed()
      .toPromise();
  }

  isSelected$(media: WithRef<IReferralMedia>): Observable<boolean> {
    return this.selectionList.isSelected$(media);
  }

  toggleSelectMode(): void {
    this.selectMode.set(!this.selectMode());
    if (!this.selectMode()) {
      this.selectionList.resetSelected();
    }
  }

  async toggleSelectionOrView(
    media: WithRef<IReferralMedia>[],
    selected: WithRef<IReferralMedia>
  ): Promise<void> {
    if (this.selectMode()) {
      this.selectionList.toggleSelected(selected);
      return;
    }
    await this.viewMedia(media, selected);
  }

  async downloadSelected(): Promise<void> {
    const selected = await snapshot(this.selectionList.selected$);
    await this._fileDownload.downloadFiles('files', selected);
  }

  async deleteSelected(): Promise<void> {
    const selected = await snapshot(this.selectionList.selected$);

    if (!this.canDeleteSelected(selected) || !selected.length) {
      return;
    }

    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, any, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({
          data: confirmationDialogData({
            title: 'Delete Selected Media',
            prompt: `Are you sure you want to delete ${selected.length} selected media items? This action cannot be undone.`,
            submitLabel: 'Delete',
            submitColor: 'warn',
          }),
        })
      )
      .afterClosed()
      .toPromise();

    if (!confirmed) {
      return;
    }

    const referral = this.referral();
    const currentUser = this.currentUser();
    if (!currentUser) {
      return;
    }

    await this.referralMediaService.delete(selected, referral, currentUser);
    this.selectionList.resetSelected();
  }

  async addMedia(event: IUploadStartedEvent): Promise<void> {
    const referral = this.referral();
    const actor = this.currentUser();

    if (!actor) {
      return;
    }

    await this.referralMediaService.add(event, referral, actor);
  }

  private _groupMedia(
    media: WithRef<IReferralMedia>[]
  ): IGroupedReferralMediaByDate[] {
    const groupedByDate = groupBy(media, (item) => toISODate(item.createdAt));
    const sortedDates = Object.keys(groupedByDate).sort();

    return sortedDates.map((date) => {
      const groupedByUploader = groupBy(
        groupedByDate[date],
        (item) => item.actor.name || 'Unknown'
      );
      const uploaders: IGroupedReferralMediaUploader[] = Object.keys(
        groupedByUploader
      ).map((uploader) => ({
        uploader,
        media: groupedByUploader[uploader],
      }));
      return { date, uploaders };
    });
  }

  private _scrollToBottom(): void {
    const container = this.mediaContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }
}
